import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import { SwiperSlide } from "swiper/react";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { ContentItemMagazinesStyleArticleMoreOnThis } from "scmp-app/components/content/content-item-render/variants/magazines-style-article-more-on-this";
import { useContentReadMoreContext } from "scmp-app/components/content/content-read-more";
import type { articleMoreOnThisListStyleArticle$key } from "scmp-app/queries/__generated__/articleMoreOnThisListStyleArticle.graphql";

import { Container, ItemContainer, StyledSwiper, Title } from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreOnThisListStyleArticle$key;
};

export const ArticleMoreOnThisList: FunctionComponent<Props> = ({ className, reference }) => {
  const { styleMoreOnThisArticles } = useFragment(
    graphql`
      fragment articleMoreOnThisListStyleArticle on Article {
        styleMoreOnThisArticles: moreOnThisArticles {
          ...entityLink
          ...magazinesStyleArticleMoreOnThisContentItemContent
          entityId
        }
      }
    `,
    reference,
  );

  const articles = styleMoreOnThisArticles?.filter(notEmpty).slice(0, 5);

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), true);
  const { isReadMoreClicked } = useContentReadMoreContext();

  if (isMobile && !isReadMoreClicked) return null;
  if (!articles?.length) return null;

  return (
    <Container className={className}>
      <Title>
        / READ M<span>O</span>RE /
      </Title>
      <ItemContainer>
        <StyledSwiper
          $center={articles.length < 3}
          breakpoints={{
            768: {
              noSwiping: false,
              slidesPerView: 3,
              spaceBetween: 20,
            },
            1024: {
              noSwiping: false,
              slidesPerView: 3,
              spaceBetween: 32,
            },
          }}
          centeredSlidesBounds={true}
          noSwiping={true}
          pagination={false}
          slidesPerView={1}
        >
          {articles.map(article => (
            <SwiperSlide key={article.entityId}>
              <ContentItemMagazinesStyleArticleMoreOnThis reference={article} />
            </SwiperSlide>
          ))}
        </StyledSwiper>
      </ItemContainer>
    </Container>
  );
};

ArticleMoreOnThisList.displayName = "ArticleMoreOnThisList";
